import { Routes, Route } from 'react-router-dom'
import ProtectedRoute from './components/ProtectedRoute'
import AppShell from './components/AppShell'
import { ProjectContext } from './context/ProjectContext'
import TenderListPage from './pages/TenderListPage'
import AdminCustomerTendersPage from './pages/AdminCustomerTendersPage'
import CostCodeDictionaryPage from './pages/CostCodeDictionaryPage'
import ResourceDictionaryPage from './pages/ResourceDictionaryPage'
import TenderWorkspacePage from './pages/tender/TenderWorkspacePage'
import RevisionWorkspacePage from './pages/tender/RevisionWorkspacePage'
import RateBuildUpPage from './pages/tender/RateBuildUpPage'
import FirstPrinciplesLibraryPage from './pages/tender/FirstPrinciplesLibraryPage'
import TakeoffPage from './pages/tender/TakeoffPage'
import TakeoffReportPage from './pages/tender/TakeoffReportPage'
import MarkupPage from './pages/tender/MarkupPage'
import IndirectRiskHoPage from './pages/tender/IndirectRiskHoPage'
import DirectBudgetReviewPage from './pages/tender/DirectBudgetReviewPage'
import IndirectBudgetReviewPage from './pages/tender/IndirectBudgetReviewPage'
import TenderSummaryPage from './pages/tender/TenderSummaryPage'
import UnpricedItemsPage from './pages/tender/UnpricedItemsPage'
import EstimatingKPIsPage from './pages/tender/EstimatingKPIsPage'
import RevisionComparisonPage from './pages/tender/RevisionComparisonPage'
import BasisOfEstimatePage from './pages/tender/BasisOfEstimatePage'
import LifecyclePage from './pages/tender/LifecyclePage'
import BudgetSummaryPage from './pages/tender/BudgetSummaryPage'

// NOTE: This component no longer owns a <BrowserRouter> -- it is mounted
// by CostSpace's App.jsx as <Route path="estimation/*" element={<App/>}/>.
// All paths below are RELATIVE to that "estimation/" boundary (no leading
// "/"), so React Router resolves them as "estimation/tender/:tenderId" etc.
// against the single global BrowserRouter owned by CostSpace.
//
// Gate 1 integration fix: Estimation's own <AuthProvider> and its
// "login" route are removed. CostSpace's AppInner already refuses to
// render ANY case in its switch -- including 'estimation' -- unless an
// authenticated user AND profile exist (see CostSpace App.jsx: `if
// (!user) return <LoginPage/>`); CostSpace's LoginPage is the only sign-in
// surface for the whole integrated app now. ProtectedRoute/AppShell below
// still gate this subtree using that same session (via the shared
// AuthContext they now import directly), as a defense-in-depth check, not
// as a second authentication system. Every page component is untouched.
//
// Project Context Bridge (Phase 1): selectedProject is passed in verbatim
// from CostSpace's AppShell (its own local state -- no CostSpace Context
// exists to consume instead, confirmed by inspection) and exposed
// read-only via ProjectContext to any Estimation page that opts in.
// tenderId/revisionId remain sourced from useParams() everywhere below,
// unchanged -- this context carries no financial data and no setter, and
// none of the 17 routes or their element props change.
export default function App({ selectedProject }) {
  return (
    <ProjectContext.Provider value={selectedProject ?? null}>
      <Routes>
        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route path="" element={<TenderListPage />} />
          {/* Platform-admin only (UI hint); every operation is re-checked in the database. */}
          <Route path="admin/customer-tenders" element={<AdminCustomerTendersPage />} />
          {/* Tender-scoped only, per the approved architecture -- there
              is no global/company-wide Dictionary entry point anymore.
              The two routes below are only reachable from inside a
              specific Tender, so tenderId is always present (never
              "undefined" in the Supabase query). */}
          <Route path="tender/:tenderId/cost-codes" element={<CostCodeDictionaryPage />} />
          <Route path="tender/:tenderId/resources" element={<ResourceDictionaryPage />} />
          <Route path="tender/:tenderId" element={<TenderWorkspacePage />} />
          <Route path="tender/:tenderId/revision/:revisionId" element={<RevisionWorkspacePage />} />
          <Route path="tender/:tenderId/revision/:revisionId/rate-build-up" element={<RateBuildUpPage />} />
          <Route path="tender/:tenderId/first-principles" element={<FirstPrinciplesLibraryPage />} />
          <Route path="tender/:tenderId/revision/:revisionId/takeoff" element={<TakeoffPage />} />
          <Route path="tender/:tenderId/revision/:revisionId/takeoff-report" element={<TakeoffReportPage />} />
          <Route path="tender/:tenderId/revision/:revisionId/markup" element={<MarkupPage />} />
          <Route path="tender/:tenderId/revision/:revisionId/indirect" element={<IndirectRiskHoPage />} />
          <Route path="tender/:tenderId/revision/:revisionId/direct-budget-review" element={<DirectBudgetReviewPage />} />
          <Route path="tender/:tenderId/revision/:revisionId/indirect-budget-review" element={<IndirectBudgetReviewPage />} />
          <Route path="tender/:tenderId/revision/:revisionId/summary" element={<TenderSummaryPage />} />
          <Route path="tender/:tenderId/revision/:revisionId/unpriced" element={<UnpricedItemsPage />} />
          <Route path="tender/:tenderId/revision/:revisionId/kpis" element={<EstimatingKPIsPage />} />
          <Route path="tender/:tenderId/compare" element={<RevisionComparisonPage />} />
          <Route path="tender/:tenderId/revision/:revisionId/basis" element={<BasisOfEstimatePage />} />
          <Route path="tender/:tenderId/revision/:revisionId/lifecycle" element={<LifecyclePage />} />
          <Route path="tender/:tenderId/revision/:revisionId/budget-summary" element={<BudgetSummaryPage />} />
        </Route>
      </Routes>
    </ProjectContext.Provider>
  )
}
